import ImagePlaceholder from './ImagePlaceholder';
import LanguageSelector from './LanguageSelector';
import { TranslationData } from './Translation';
import { useStoreConfigContext } from '@/components/config-editor/providers';
import { setNestedValue } from '@/components/config-editor/utils/set-nested-value';
import { ImageUpload } from './ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LanguageCode } from '@/data/languages';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { useState } from 'react';

interface SectionData {
  iconUrl: string;
  titleTrans: TranslationData;
  descriptionTrans: TranslationData;
}

interface SectionProps {
  section: SectionData;
  index: number;
  handleDeleteSection: (index: number) => void;
  handleChangeSection: (index: number, fieldName: string, value: string) => void;
  handleChangeLanguage: (languageCode: LanguageCode) => void;
  disabled?: boolean;
}

function Section(props: SectionProps) {
  const { section, index, handleDeleteSection, handleChangeSection, handleChangeLanguage, disabled } = props;
  const [open, setOpen] = useState(false);
  const [languageCode, setLanguageCode] = useState<LanguageCode>('en');

  const handleChangeLanguageCode = (languageCode: LanguageCode) => {
    setLanguageCode(languageCode);
    handleChangeLanguage(languageCode);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-2" key={`section-${index}`}>
      <div className="flex items-center justify-between gap-2">
        <p>Section </p>
        <div className="flex items-center gap-2">
          <LanguageSelector
            open={open}
            setOpen={setOpen}
            languageCode={languageCode}
            setLanguageCode={handleChangeLanguageCode}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="destructive"
            onClick={() => handleDeleteSection(index)}
            disabled={disabled}
          >
            <TrashIcon />
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {section.iconUrl ? (
          <img className="h-auto w-20" src={section.iconUrl} alt="Section image" />
        ) : (
          <ImagePlaceholder />
        )}
        <div className="flex flex-grow flex-col gap-2">
          <Input
            value={section.titleTrans[languageCode]}
            onChange={(e) => handleChangeSection(index, `titleTrans.${languageCode}`, e.target.value)}
            disabled={disabled}
          />
          <Input
            value={section.descriptionTrans[languageCode]}
            onChange={(e) => handleChangeSection(index, `descriptionTrans.${languageCode}`, e.target.value)}
            disabled={disabled}
          />
          <div className="flex w-full items-start gap-2 [&>*]:w-1/2">
            <Input
              value={section.iconUrl}
              onChange={(e) => handleChangeSection(index, 'iconUrl', e.target.value)}
              disabled={disabled}
            />
            <ImageUpload
              url={section.iconUrl}
              onImageAvailable={(url) => handleChangeSection(index, 'iconUrl', url)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export type SectionsData = SectionData[];

interface SectionsProps {
  value?: SectionsData;
  title?: string;
  onChange: (value: SectionsData) => void;
  disabled?: boolean;
}

function Sections({ value, title, onChange, disabled }: SectionsProps) {
  const { config } = useStoreConfigContext();

  const sections =
    value ??
    config?.sections.map((s) => ({
      iconUrl: s.iconUrl,
      titleTrans: s.titleTrans,
      descriptionTrans: s.descriptionTrans,
    })) ??
    [];

  const handleAddSection = () => {
    const newSection: SectionData = {
      descriptionTrans: { en: '' },
      titleTrans: { en: '' },
      iconUrl: '',
    };
    onChange(sections?.concat(newSection) || [newSection]);
  };

  const handleDeleteSection = (index: number) => {
    const newSections = sections?.filter((_, i) => i !== index);
    onChange(newSections);
  };

  const handleChangeSection = (index: number, fieldName: string, value: string) => {
    const newSections = sections?.map((s, i) => (i === index ? setNestedValue(s, fieldName, value) : s));
    onChange(newSections);
  };

  const addNewLanguageToTranslation = (
    section: SectionData,
    property: 'titleTrans' | 'descriptionTrans',
    languageCode: LanguageCode,
  ): TranslationData => {
    const currentProperty = section[property];
    if (currentProperty[languageCode]) {
      return currentProperty;
    }
    return { ...currentProperty, [languageCode]: '' };
  };

  const handleChangeLanguage = (languageCode: LanguageCode) => {
    const newSections = sections.map((s) => ({
      iconUrl: s.iconUrl,
      titleTrans: addNewLanguageToTranslation(s, 'titleTrans', languageCode),
      descriptionTrans: addNewLanguageToTranslation(s, 'descriptionTrans', languageCode),
    }));
    onChange(newSections);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        {sections?.map((section, index) => (
          <Section
            key={`section-${index}`}
            section={section}
            index={index}
            handleDeleteSection={handleDeleteSection}
            handleChangeSection={handleChangeSection}
            handleChangeLanguage={handleChangeLanguage}
            disabled={disabled}
          />
        ))}
      </div>
      <Button type="button" onClick={() => handleAddSection()} disabled={disabled}>
        <PlusIcon />
        Add section
      </Button>
    </div>
  );
}

export default Sections;
